import { useState, useMemo, useRef, useEffect } from 'react';
import { Wand2, Sparkles, Scale, BookText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface LegalStyleIndicatorProps {
  conversationId: string | null;
  currentTemplate?: string;
  onTemplateChange?: (templateName: string) => void;
}

type TemplateKey =
  | 'lovable_gpt'
  | 'conversational'
  | 'formal_legal'
  | 'technical'
  | 'creative'
  | 'abnt_juridico';

const OPTIONS: Array<{ key: TemplateKey; label: string; icon: any }> = [
  { key: 'lovable_gpt',    label: 'Padrão Laib',     icon: Sparkles },
  { key: 'conversational',  label: 'Conversacional',  icon: Wand2 },
  { key: 'formal_legal',    label: 'Formal Jurídico', icon: Scale },
  { key: 'technical',       label: 'Técnico',         icon: BookText },
  { key: 'creative',        label: 'Criativo',        icon: Sparkles },
  { key: 'abnt_juridico',   label: 'ABNT Jurídico',   icon: BookText },
];

export function LegalStyleIndicator({
  conversationId,
  currentTemplate,
  onTemplateChange,
}: LegalStyleIndicatorProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const current: TemplateKey = useMemo(
    () => (currentTemplate as TemplateKey) || 'lovable_gpt',
    [currentTemplate]
  );

  const CurrentIcon = useMemo(
    () => OPTIONS.find((o) => o.key === current)?.icon ?? Sparkles,
    [current]
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleChange = async (key: TemplateKey) => {
    if (!conversationId) {
      toast({ title: 'Erro', description: 'Nenhuma conversa ativa', variant: 'destructive' });
      return;
    }
    try {
      const { error } = await supabase
        .from('legal_conversations')
        .update({ metadata: { template: key }, updated_at: new Date().toISOString() })
        .eq('id', conversationId);
      if (error) throw error;
      onTemplateChange?.(key);
      toast({
        title: 'Estilo alterado',
        description: `Agora usando: ${OPTIONS.find((o) => o.key === key)?.label}`,
        duration: 2000,
      });
      setOpen(false);
    } catch (error) {
      console.error('Erro ao atualizar template:', error);
      toast({ title: 'Erro', description: 'Não foi possível alterar o estilo', variant: 'destructive' });
    }
  };

  const currentLabel = OPTIONS.find((o) => o.key === current)?.label ?? 'Padrão Laib';

  return (
    <div ref={wrapperRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Abrir seletor de estilo do chat"
        className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-border bg-background text-sm text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
      >
        <CurrentIcon className="w-4 h-4 shrink-0" />
        <span className="hidden sm:inline whitespace-nowrap">{currentLabel}</span>
        <span className="sm:hidden whitespace-nowrap">Tom</span>
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute left-0 bottom-[calc(100%+0.5rem)] w-[280px] bg-background border border-border rounded-xl shadow-lg p-2 z-[70]">
          {OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const active = opt.key === current;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => handleChange(opt.key)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-foreground transition-colors cursor-pointer ${
                  active
                    ? 'bg-primary/10 border border-primary/30'
                    : 'border border-transparent hover:bg-accent'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
